import { Message } from '@mairistem/composants';
import { Sens } from '@mairistem/comptabilite';
import {
	fetchProduitActif,
	getLibelleImputation,
	Produit,
} from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import type Simulation from '../../../../interfaces/simulation/simulation';
import { getInfoImputationsApcp } from '../../../../services/simulation/apcp';
import ImportTable, { type ImportData } from './ImportTable';

interface ImportApcpProps {
	onClose: () => void;
	onValidate: (resultat?: string) => void;
	simulation: Simulation;
	hasCa: boolean;
}

const ImportApcp = ({
	onClose,
	onValidate,
	simulation,
	hasCa,
}: ImportApcpProps) => {
	const [loading, setLoading] = React.useState(true);
	const [apcpListe, setApcpListe] = React.useState<ImportData[]>([]);
	const [produitApcpActif, setProduitApcpActif] = React.useState<
		boolean | null
	>(null);

	React.useEffect(() => {
		const getProduitActif = async () => {
			setProduitApcpActif(await fetchProduitActif(Produit.APCP));
		};

		getProduitActif();
	}, []);

	const fetchImportApcp = React.useCallback(async () => {
		try {
			setLoading(true);
			const data = await getInfoImputationsApcp(
				new URLSearchParams({
					sens: Sens.DEPENSE,
					identifiantExercice: simulation.identifiantExercice.toString(),
					hasCa: hasCa ? '1' : '0',
				}),
			);
			setApcpListe(
				data.map((apcp, i) => ({
					key: i,
					...apcp,
					children: apcp.imputations?.map((i) => ({
						...i,
						key: i.identifiantProImputation,
						libelleDetail: i.libelleService,
						libelle: getLibelleImputation(
							i.sens,
							i.section,
							i.chapitre,
							i.article,
							i?.operation,
							i?.fonction,
							i?.ventilation,
							i?.analytique,
						),
					})),
				})),
			);
		} catch (_e) {
			toast.error('Erreur lors du chargement des APCP');
		} finally {
			setLoading(false);
		}
	}, [simulation.identifiantExercice, hasCa]);

	React.useEffect(() => {
		if (produitApcpActif) {
			fetchImportApcp();
		}
	}, [fetchImportApcp, produitApcpActif]);

	if (produitApcpActif === null) {
		return null;
	}

	if (!produitApcpActif) {
		return (
			<div className="information">
				<Message
					icon="info"
					header="APCP"
					content="Le produit APCP n'est pas sérialisé."
				/>
			</div>
		);
	}

	return (
		<ImportTable
			simulation={simulation}
			onClose={onClose}
			onValidate={onValidate}
			loading={loading}
			data={apcpListe}
			withSubstitution={false}
			hasCa={hasCa}
			hasApcp
		/>
	);
};

export default ImportApcp;
